import * as Tone from "tone";
import { Midi } from "tonal";
import { getConfigValue } from "./config";

// Global synth instance
let synth = null;
let isPlaying = false;
let currentlyPlayingScale = null;

/**
 * Initialize the audio system
 */
export const initAudio = async () => {
  if (!synth) {
    // Create a high-quality piano-like synthesizer
    synth = new Tone.PolySynth(Tone.Synth, {
      oscillator: {
        type: "sine"
      },
      envelope: {
        attack: 0.02,
        decay: 0.2,
        sustain: 0.3,
        release: 0.8
      },
      filter: {
        Q: 2,
        frequency: 800,
        rolloff: -12
      },
      filterEnvelope: {
        attack: 0.02,
        decay: 0.1,
        sustain: 0.5,
        release: 1.2,
        baseFrequency: 300,
        octaves: 2
      }
    }).toDestination();
    
    // Add some reverb for richness
    const reverb = new Tone.Reverb({
      decay: 2,
      wet: 0.3
    }).toDestination();
    
    synth.connect(reverb);
  }
};

/**
 * Convert scale array to MIDI note numbers
 * @param {Array} scale - Array of semitone intervals
 * @param {number} startingPitch - Starting MIDI note (default from config)
 * @returns {Array} Array of MIDI note numbers
 */
export const scaleToMidiNotes = (scale, startingPitch = getConfigValue('audio.pitch.startingPitch', 60)) => {
  const midiNotes = scale.reduce((acc, step) => {
    const lastNote = acc[acc.length - 1] || startingPitch;
    acc.push(lastNote + step);
    return acc;
  }, [startingPitch]);
  
  return midiNotes;
};

/**
 * Play a single note
 * @param {number} midiNote - MIDI note number
 * @param {number} duration - Duration in seconds
 */
export const playNote = async (midiNote, duration = 0.5) => {
  await initAudio();
  
  if (Tone.context.state !== 'running') {
    await Tone.start();
  }
  
  const frequency = Midi.midiToFreq(midiNote);
  synth.triggerAttackRelease(frequency, duration);
};

/**
 * Play a single note with visual highlighting
 * @param {number} midiNote - MIDI note number to play
 * @param {HTMLElement} container - Container with VexFlow SVG
 * @param {number} noteIndex - Index of the note to highlight
 * @param {number} duration - Duration in seconds (default from config)
 */
export const playNoteWithHighlight = async (midiNote, container, noteIndex, duration = null) => {
  // Stop any current playback
  stopAudio();
  
  await initAudio();
  
  if (Tone.context.state !== 'running') {
    await Tone.start();
  }
  
  const noteDuration = duration || getConfigValue('audio.tempo.noteDuration', 0.3);
  
  // Highlight the note
  if (container) {
    highlightNote(container, noteIndex);
  }
  
  // Play the note
  const frequency = Midi.midiToFreq(midiNote);
  synth.triggerAttackRelease(frequency, noteDuration);
  
  // Remove highlight after note duration
  setTimeout(() => {
    if (container) {
      unhighlightNote(container, noteIndex);
    }
  }, noteDuration * 1000);
};

/**
 * Play a scale as an ascending sequence with visual highlighting
 * @param {Array} scale - Array of semitone intervals
 * @param {Object} options - Playback options
 * @param {number} options.startingPitch - Starting MIDI note (default from config)
 * @param {number} options.noteDuration - Duration of each note in seconds (default from config)
 * @param {number} options.noteGap - Gap between notes in seconds (default from config)
 * @param {boolean} options.playDescending - Also play descending after ascending (default: false)
 * @param {HTMLElement} options.container - Container element for note highlighting
 * @param {Function} options.onNoteStart - Callback when note starts (noteIndex)
 * @param {Function} options.onNoteEnd - Callback when note ends (noteIndex)
 * @param {Function} options.onStop - Callback when playback stops
 */
export const playScale = async (scale, options = {}) => {
  const {
    scaleId,
    startingPitch = getConfigValue('audio.pitch.startingPitch', 60),
    noteDuration = getConfigValue('audio.tempo.noteDuration', 0.3),
    noteGap = getConfigValue('audio.tempo.noteGap', 0.2),
    playDescending = false,
    container,
    onNoteStart,
    onNoteEnd,
    onStop
  } = options;
  
  if (isPlaying) return; // Prevent overlapping playback
  
  await initAudio();
  
  if (Tone.context.state !== 'running') {
    await Tone.start();
  }
  
  isPlaying = true;
  if (scaleId) {
    currentlyPlayingScale = scaleId;
  }
  
  try {
    const midiNotes = scaleToMidiNotes(scale, startingPitch);
    
    // Play ascending
    for (let i = 0; i < midiNotes.length; i++) {
      if (!isPlaying) break; // Allow stopping mid-playback
      
      // Highlight note start
      if (onNoteStart) onNoteStart(i);
      if (container) highlightNote(container, i);
      
      const frequency = Midi.midiToFreq(midiNotes[i]);
      synth.triggerAttackRelease(frequency, noteDuration);
      
      // Wait for note duration
      await new Promise(resolve => setTimeout(resolve, noteDuration * 1000));
      
      // Remove highlight
      if (onNoteEnd) onNoteEnd(i);
      if (container) unhighlightNote(container, i);
      
      // Wait for gap
      if (i < midiNotes.length - 1) {
        await new Promise(resolve => setTimeout(resolve, noteGap * 1000));
      }
    }
    
    // Play descending if requested
    if (playDescending && isPlaying) {
      // Small pause between ascending and descending
      await new Promise(resolve => setTimeout(resolve, 200));
      
      for (let i = midiNotes.length - 2; i >= 0; i--) {
        if (!isPlaying) break;
        
        if (onNoteStart) onNoteStart(i);
        if (container) highlightNote(container, i);
        
        const frequency = Midi.midiToFreq(midiNotes[i]);
        synth.triggerAttackRelease(frequency, noteDuration);
        
        await new Promise(resolve => setTimeout(resolve, noteDuration * 1000));
        
        if (onNoteEnd) onNoteEnd(i);
        if (container) unhighlightNote(container, i);
        
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, noteGap * 1000));
        }
      }
    }
  } finally {
    isPlaying = false;
    if (container) clearAllHighlights(container);
    if (onStop) onStop();
  }
};

/**
 * Play scale as a chord (all notes at once)
 * @param {Array} scale - Array of semitone intervals
 * @param {Object} options - Playback options
 * @param {number} options.startingPitch - Starting MIDI note (default: 60)
 * @param {number} options.duration - Chord duration in seconds (default: 2)
 */
export const playScaleAsChord = async (scale, options = {}) => {
  const {
    startingPitch = 60,
    duration = 2
  } = options;
  
  await initAudio();
  
  if (Tone.context.state !== 'running') {
    await Tone.start();
  }
  
  const midiNotes = scaleToMidiNotes(scale, startingPitch);
  const frequencies = midiNotes.map(note => Midi.midiToFreq(note));
  
  synth.triggerAttackRelease(frequencies, duration);
};

/**
 * Stop all currently playing audio
 */
export const stopAudio = () => {
  if (synth) {
    synth.releaseAll();
  }
  isPlaying = false;
  currentlyPlayingScale = null;
};

/**
 * Check if audio is currently playing
 * @returns {boolean}
 */
export const getIsPlaying = () => isPlaying;

/**
 * Set the currently playing scale
 * @param {string} scaleId - Unique identifier for the scale
 */
export const setCurrentlyPlayingScale = (scaleId) => {
  currentlyPlayingScale = scaleId;
};

/**
 * Get the currently playing scale
 * @returns {string|null}
 */
export const getCurrentlyPlayingScale = () => currentlyPlayingScale;

const DEBUG = false; // Set to true for audio/highlight debugging

/**
 * Highlight a specific note in the VexFlow SVG
 * @param {HTMLElement} container - Container with VexFlow SVG
 * @param {number} noteIndex - Index of the note to highlight (0-based)
 */
export const highlightNote = (container, noteIndex) => {
  if (!container) return;
  
  const svg = container.querySelector('svg');
  if (!svg) return;
  
  const staveNotes = svg.querySelectorAll('.vf-stavenote');
  
  if (staveNotes[noteIndex]) {
    staveNotes[noteIndex].classList.add('note-highlight-container');
    
    // Add glow-only class if specified in config
    if (getConfigValue('visual.animation.useGlowOnly', true)) {
      staveNotes[noteIndex].classList.add('glow-only');
    }
  }
};

/**
 * Remove highlight from a specific note
 * @param {HTMLElement} container - Container with VexFlow SVG
 * @param {number} noteIndex - Index of the note to unhighlight
 */
export const unhighlightNote = (container, noteIndex) => {
  if (!container) return;
  
  const svg = container.querySelector('svg');
  if (!svg) return;
  
  const staveNotes = svg.querySelectorAll('.vf-stavenote');
  
  if (staveNotes[noteIndex]) {
    staveNotes[noteIndex].classList.remove('note-highlight-container');
    staveNotes[noteIndex].classList.remove('glow-only');
  }
};

/**
 * Clear all note highlights
 * @param {HTMLElement} container - Container with VexFlow SVG
 */
export const clearAllHighlights = (container) => {
  if (!container) return;
  
  const svg = container.querySelector('svg');
  if (!svg) return;
  
  const highlightedContainers = svg.querySelectorAll('.note-highlight-container');
  highlightedContainers.forEach(element => {
    element.classList.remove('note-highlight-container');
  });
};

/**
 * Get a display-friendly note name for a MIDI number
 * @param {number} midiNote - MIDI note number
 * @returns {string} Note name (e.g., "C4", "F#5")
 */
export const getMidiNoteName = (midiNote) => {
  return Midi.midiToNoteName(midiNote);
};